// ---- module-level helpers ------------------------------------------------

function _cartGetLiveRegion(){
	let region = document.getElementById('CartLiveRegion');
	if ( !region ) {
		region = document.createElement('div');
		region.id = 'CartLiveRegion';
		region.setAttribute('role', 'status');
		region.setAttribute('aria-live', 'polite');
		region.setAttribute('aria-atomic', 'true');
		region.className = 'visually-hidden';
		document.body.appendChild(region);
	}
	return region;
}

function _cartGetAnchorProductId(){
	const ajaxForm = document.getElementById('AjaxCartForm');
	if ( !ajaxForm ) return null;
	const firstItem = ajaxForm.querySelector('[data-js-cart-item]');
	return firstItem ? firstItem.dataset.productId : null;
}

function _cartRefreshRecommendationsIfAnchorChanged(prevAnchor){
	const currentAnchor = _cartGetAnchorProductId();
	if ( prevAnchor === currentAnchor ) return;
	const recs = document.querySelector('cart-recommendations');
	if ( recs && typeof recs.generateRecommendations === 'function' ) {
		recs.innerHTML = '';
		recs.generateRecommendations();
	}
}

const _CART_FOCUS_ROLES = ['qty-plus', 'qty-minus', 'qty', 'remove'];

function _cartSnapshotFocus(){
	const active = document.activeElement;
	if ( !active || active === document.body ) return null;
	const cartItem = active.closest && active.closest('[data-js-cart-item]');
	if ( !cartItem ) return null;
	const role = _CART_FOCUS_ROLES.find(r => active.classList.contains(r));
	if ( !role ) return null;
	return { line: cartItem.dataset.line, role, selectionStart: active.selectionStart, selectionEnd: active.selectionEnd };
}

function _cartRestoreFocus(snapshot){
	if ( !snapshot ) return;
	const cartItem = document.querySelector(`#AjaxCartForm [data-js-cart-item][data-line="${snapshot.line}"]`);
	if ( !cartItem ) return;
	const target = cartItem.querySelector('.' + snapshot.role);
	if ( !target || typeof target.focus !== 'function' ) return;
	try { target.focus({ preventScroll: true }); } catch(e) { target.focus(); }
	if ( snapshot.role === 'qty' && typeof target.setSelectionRange === 'function' && snapshot.selectionStart != null ) {
		try { target.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd); } catch(e) {}
	}
}

function _cartAnnounceUpdate(){
	const region = _cartGetLiveRegion();
	const ajaxForm = document.getElementById('AjaxCartForm');
	if ( !ajaxForm ) return;
	const countEl = ajaxForm.querySelector('[data-cart-count]');
	const totalEl = ajaxForm.querySelector('[data-cart-total]');
	const count = countEl ? countEl.textContent.trim() : '';
	const total = totalEl ? totalEl.textContent.trim() : '';
	const template = window.KROWN && KROWN.settings && KROWN.settings.locales && KROWN.settings.locales.cart_updated_announcement;
	const message = template
		? template.replace('$COUNT', count).replace('$TOTAL', total)
		: `Cart updated. ${count} items, total ${total}.`;
	// Toggle to force re-announcement even when the message text is identical.
	region.textContent = '';
	region.textContent = message;
}

function _cartApplySectionResponse(text){
	const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
	const cartFormSource = sectionInnerHTML.getElementById('AjaxCartForm');
	const cartSubtotalSource = sectionInnerHTML.getElementById('AjaxCartSubtotal');
	if ( !cartFormSource || !cartSubtotalSource ) return false;

	const cartItems = document.getElementById('AjaxCartForm');
	cartItems.innerHTML = cartFormSource.innerHTML;
	if ( typeof cartItems.ajaxifyCartItems === 'function' ) {
		cartItems.ajaxifyCartItems();
	}

	const countSource = cartItems.querySelector('[data-cart-count]');
	const totalSource = cartItems.querySelector('[data-cart-total]');
	if ( countSource ) {
		document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
			elm.textContent = countSource.textContent;
		});
	}
	if ( totalSource ) {
		document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
			elm.textContent = totalSource.textContent;
		});
	}

	document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalSource.innerHTML;
	return true;
}

// ---- CartForm -----------------------------------------------------------

if ( typeof CartForm !== 'function' ) {
	class CartForm extends HTMLElement {

		constructor(){
			super();
			this._abortController = null;
			this.ajaxifyCartItems();
		}

		ajaxifyCartItems(){

			this.form = this.querySelector('form');

			this.querySelectorAll('[data-js-cart-item]').forEach(item=>{

				const remove = item.querySelector('.remove');
				if ( remove ) {
					remove.dataset.href = remove.getAttribute('href');
					remove.setAttribute('href', '');
					remove.addEventListener('click', (e)=>{
						e.preventDefault();
						this.updateCartQty(item, 0);
					})
				}

				const qty = item.querySelector('.qty');
				if ( qty ) {
					qty.addEventListener('input', debounce(e=>{
						e.preventDefault();
						e.target.blur();
						this.updateCartQty(item, parseInt(qty.value));
					}, 500));
					qty.addEventListener('click', (e)=>{
						e.target.select();
					})
				}

			})

		}

		updateCartQty(item, newQty){

			// Cancel any in-flight cart update so the latest qty wins.
			if ( this._abortController ) {
				this._abortController.abort();
			}
			this._abortController = new AbortController();
			const signal = this._abortController.signal;

			const anchorBefore = _cartGetAnchorProductId();
			const focusSnapshot = _cartSnapshotFocus();

			let stockAlert = null;

			this.form.classList.add('processing');
			if ( this.querySelector('.alert') ) {
				this.querySelector('.alert').remove();
			}

			const body = JSON.stringify({
				line: parseInt(item.dataset.line),
				quantity: newQty
			});

			fetch(KROWN.settings.routes.cart_change_url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
					body,
					signal
				})
				.then(response => response.json())
				.then(response => {
					if ( response.status === 422 ) {
						stockAlert = document.createElement('span');
						stockAlert.classList.add('alert', 'alert--error');
						stockAlert.textContent = ( typeof response.description === 'string' )
							? response.description
							: response.message;
					}
					return fetch('?section_id=helper-cart', { signal });
				})
				.then(response => response.text())
				.then(text => {

					if ( !_cartApplySectionResponse(text) ) return;

					if ( stockAlert !== null ) {
						const liveForm = this.querySelector('form');
						if ( liveForm ) liveForm.prepend(stockAlert);
					}

					_cartRestoreFocus(focusSnapshot);
					_cartAnnounceUpdate();
					_cartRefreshRecommendationsIfAnchorChanged(anchorBefore);

					this.dispatchEvent(new Event('cart-updated'));

				})
				.catch(e => {
					if ( e && e.name === 'AbortError' ) return;
					console.log(e);
					const liveForm = this.querySelector('form') || this.form;
					if ( liveForm ) {
						const existingAlert = liveForm.querySelector(':scope > .alert');
						if ( existingAlert ) existingAlert.remove();
						const alert = document.createElement('span');
						alert.classList.add('alert', 'alert--error');
						alert.textContent = KROWN.settings.locales.cart_general_error;
						liveForm.prepend(alert);
					}
				})
				.finally(() => {
					if ( this._abortController && this._abortController.signal === signal ) {
						this._abortController = null;
					}
					const liveForm = this.querySelector('form') || this.form;
					if ( liveForm ) liveForm.classList.remove('processing');
				});
		}

	}


  if ( typeof customElements.get('cart-form') == 'undefined' ) {
		customElements.define('cart-form', CartForm);
	}

}

// ---- CartProductQuantity ------------------------------------------------

if ( typeof CartProductQuantity !== 'function' ) {

	class CartProductQuantity extends HTMLElement {
		constructor(){
			super();
			// Per-instance debounce coalesces rapid +/- clicks into a single AJAX call
			// using the latest qty value (set optimistically by the outer <product-quantity>).
			this._debouncedUpdate = debounce(()=>{
				const ajaxForm = document.getElementById('AjaxCartForm');
				const item = this.closest('[data-js-cart-item]');
				const input = this.querySelector('.qty');
				if ( ajaxForm && item && input ) {
					ajaxForm.updateCartQty(item, parseInt(input.value));
				}
			}, 300);
			this.querySelector('.qty-minus').addEventListener('click', this.changeCartInput.bind(this));
			this.querySelector('.qty-plus').addEventListener('click', this.changeCartInput.bind(this));
		}
		changeCartInput(){
			this._debouncedUpdate();
		}
	}

  if ( typeof customElements.get('cart-product-quantity') == 'undefined' ) {
		customElements.define('cart-product-quantity', CartProductQuantity);
	}

}

// ---- window.refreshCart (public hook for apps) --------------------------

if ( ! window.refreshCart ) {

	window.refreshCart = () => {

		const anchorBefore = _cartGetAnchorProductId();

		fetch('?section_id=helper-cart')
			.then(response => response.text())
			.then(text => {

				if ( !_cartApplySectionResponse(text) ) return;

				if ( document.querySelector('[data-js-site-cart-sidebar]') ) {
					document.querySelector('[data-js-site-cart-sidebar]').show();
				}

				_cartAnnounceUpdate();
				_cartRefreshRecommendationsIfAnchorChanged(anchorBefore);

			})

	}

}
